<template>
  <div class="container mt-4">
    <div class="card text-center">
      <div class="card-body">
        <h5 class="card-title">User Lookup</h5>

        <!-- Always Show Email Input & Check User Button -->
        <div class="mb-3">
          <input type="email" v-model="inputEmail" class="form-control" placeholder="Enter user email" />
          <button class="btn btn-primary mt-2" @click="checkUser" :disabled="isLoading">
            <span v-if="isLoading" class="spinner-border spinner-border-sm"></span>
            {{ isLoading ? "Checking..." : "Check User" }}
          </button>
        </div>

        <!-- Show User Info if Found -->
        <div v-if="userChecked">
          <div v-if="userExists">
            <h5 class="mt-3">User Found:</h5>

            <!-- Show User Avatar -->
            <img :src="userPicture || defaultAvatar" class="rounded-circle user-avatar" alt="User Avatar" />

            <p><strong>Name:</strong> {{ userName }}</p>
            <p><strong>Email:</strong> {{ userEmail }}</p>

            <h6>User Groups:</h6>
            <p v-if="isLoadingGroups">
              <span class="spinner-border spinner-border-sm"></span> Fetching groups...
            </p>
            <ul v-else class="list-group mt-2">
              <li v-for="group in groups" :key="group.id" class="list-group-item">
                {{ group.displayName }}
              </li>
            </ul>
          </div>

          <!-- Show Guest User Creation Option if User Doesn't Exist -->
          <div v-else class="mt-3">
            <p class="text-danger">User not found in tenant.</p>
            <button class="btn btn-warning" @click="handleGuestCreation">Create as Guest User</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Logs -->
    <div class="mt-4">
      <h5>Logs:</h5>
      <pre class="log-box">{{ logs }}</pre>
    </div>
  </div>
</template>

<script>
import { checkUserExists, getUserGroups, createGuestUser, getUserProfilePicture } from '../utils/graphService';

export default {
  data() {
    return {
      inputEmail: "",
      userName: null,
      userEmail: null,
      userPicture: null,
      groups: [],
      userExists: false,
      userChecked: false,
      isLoading: false,
      isLoadingGroups: false,
      logs: "",
      tenantDomain: "paul.tech",
      defaultAvatar: "https://www.gravatar.com/avatar/?d=mp"
    };
  },
  async mounted() {
    const account = this.$msalInstance.getActiveAccount();
    if (account) {
      this.userName = account.name;

      try {
        this.userPicture = await getUserProfilePicture(this.$msalInstance, account.username);
      } catch (error) {
        console.warn("⚠️ Could not fetch profile picture", error);
      }
    }
  },
  methods: {
    async checkUser() {
      if (!this.inputEmail) {
        alert("Please enter an email address.");
        return;
      }

      this.isLoading = true;
      this.logs += `Checking user: ${this.inputEmail}...\n`;

      try {
        const existingUser = await checkUserExists(this.$msalInstance, this.inputEmail);

        if (existingUser) {
          this.userExists = true;
          this.userEmail = existingUser.mail || existingUser.userPrincipalName;
          this.userName = existingUser.displayName;
          this.logs += `User exists: ${this.userName}\n`;

          this.isLoadingGroups = true;
          this.groups = await getUserGroups(this.$msalInstance, this.inputEmail);
          this.logs += `User groups retrieved: ${this.groups.length} groups found.\n`;
        } else {
          this.userExists = false;
          this.logs += "User does not exist in the tenant.\n";
        }

        this.userChecked = true;
      } catch (error) {
        this.logs += `Error checking user: ${error.message}\n`;
      } finally {
        this.isLoading = false;
        this.isLoadingGroups = false;
      }
    },
    async handleGuestCreation() {
      if (!this.inputEmail) {
        alert("Please enter an email address.");
        return;
      }

      const userDomain = this.inputEmail.split('@')[1];
      if (userDomain === this.tenantDomain) {
        this.logs += "❌ Cannot create a guest user with the same domain as the tenant.\n";
        alert("❌ You cannot create a guest user with the same domain as the tenant.");
        return;
      }

      try {
        this.logs += "🔹 Sending invitation...\n";
        await createGuestUser(this.$msalInstance, this.inputEmail);
        this.logs += "✅ Guest user invitation sent successfully.\n";
        alert(`✅ Invitation sent to ${this.inputEmail}!`);
      } catch (error) {
        this.logs += `❌ Error creating guest user: ${error.message}\n`;
        alert(`❌ Error creating guest user: ${error.message}`);
      }
    }
  }
};
</script>

<style scoped>
.card {
  max-width: 500px;
  margin: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.user-avatar {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border: 3px solid #007bff;
  margin-top: 10px;
}
.log-box {
  background-color: #f8f9fa;
  border: 1px solid #ccc;
  padding: 10px;
  max-height: 200px;
  overflow-y: auto;
  text-align: left;
}
</style>
